import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { signAsset } from '@/api/assets';
import { uploadSignedAsset } from '@/api/misc';
import { notifyUser } from '@/store/Toast';
import type { S3AssetSignResponse, SignAssetPayload } from '@/types/Asset';
import { sanitizeResponseError } from '@/utils';

type UploadTemplateFunction = (data: { files: File[]; params: Record<string, any> }) => Promise<void>;

type OnUploadReadyFunction = ({ documents, params }: { documents: AssetFile[]; params: Record<string, any> }) => Promise<unknown>;

interface IUseDrawTemplateFileUpload {
  uploadDocuments: UploadTemplateFunction;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

type AssetFile = {
  name: string;
  key: any;
  contentType: string;
  size: number;
};

type AssetSign = {
  key: string;
  type: string;
  resource: string;
};

export const useUploadDocuments = ({
  onUploadReady: onUpload,
  resourceType,
  onSuccess,
}: {
  resourceType: 'changeOrderMilestone' | 'changeOrderDeliverable' | 'changeOrderBudgetLineItem';
  onUploadReady: OnUploadReadyFunction;
  onSuccess: () => void;
}): IUseDrawTemplateFileUpload => {
  const templateFileRef = useRef<File[]>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const dispatch = useDispatch();

  const uploadAsset = useMutation(
    ({ sign, files }: { sign: any[]; files: File[] }) =>
      Promise.allSettled(sign.map(({ value }, index) => uploadSignedAsset({ file: files[index], url: value.url }))),
    {
      onError: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to upload file(s). Try again.',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const signAssetFiles = useMutation((payloads: SignAssetPayload[]) =>
    Promise.allSettled(payloads.map((payload) => signAsset(payload) as Promise<S3AssetSignResponse>))
  );

  const patchMutation = useMutation(
    ({ documents, params }: { documents: AssetFile[]; params: Record<string, any> }) => onUpload({ documents, params }),
    {
      onSuccess: () => {
        onSuccess?.();
        setUploadSuccess(true);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const uploadDocuments: UploadTemplateFunction = async ({ files, params }) => {
    templateFileRef.current = files;

    const signPayload = files.map<AssetSign>((file) => ({
      key: file.name,
      type: file.type,
      resource: resourceType,
    }));

    // Sign asset files
    const signatures = await signAssetFiles.mutateAsync(signPayload);

    // Upload asset files to S3
    await uploadAsset.mutateAsync({
      sign: signatures,
      files: templateFileRef.current!,
    });

    // Save uploaded documents to database
    const documents: AssetFile[] = files
      .filter((_, index) => signatures[index].status === 'fulfilled')
      .map((file, index) => {
        const fulfilledSign = signatures[index] as PromiseFulfilledResult<S3AssetSignResponse>;

        return {
          name: file.name,
          key: fulfilledSign.value.fKey,
          contentType: file.type,
          size: file.size,
        };
      });

    patchMutation.mutate({ documents, params });
  };

  return {
    uploadDocuments,
    isLoading: signAssetFiles.isLoading || patchMutation.isLoading || uploadAsset.isLoading,
    isSuccess: uploadSuccess,
    isError: signAssetFiles.isError || patchMutation.isError || uploadAsset.isError,
  };
};
